import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff, Check } from 'lucide-react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useCreateCompany } from '../../hooks/api/useCompanies'
import { useUpdateUser } from '../../hooks/api/useUsers'
import { supabase } from '../../lib/supabase'
import { generateCompanySlug } from '../../utils/slug'
// Logo will be loaded from public folder

const registerSchema = z.object({
  fullName: z.string()
    .min(2, 'Full name must be at least 2 characters')
    .max(100, 'Full name must be less than 100 characters'),
  email: z.string().email('Invalid email address'),
  organizationName: z.string()
    .min(2, 'Organization name must be at least 2 characters')
    .max(100, 'Organization name must be less than 100 characters'),
  position: z.string()
    .min(2, 'Position must be at least 2 characters')
    .max(100, 'Position must be less than 100 characters'),
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/\d/, 'Password must contain a number')
    .regex(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain a symbol'),
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
})

type RegisterFormData = z.infer<typeof registerSchema>

export const RegisterForm = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const planFromUrl = searchParams.get('plan') // essentials, enhanced, premium
  const { signUp } = useAuth()
  const createCompany = useCreateCompany()
  const updateUser = useUpdateUser()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isPasswordFocused, setIsPasswordFocused] = useState(false)
  const [hasPasswordValue, setHasPasswordValue] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  
  const form = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      fullName: '',
      email: '',
      organizationName: '',
      position: '',
      password: '',
      confirmPassword: ''
    }
  })

  const { register, handleSubmit, formState: { errors }, setError, clearErrors, trigger, watch } = form
  
  // Watch both password fields for real-time validation
  const password = watch('password')
  const confirmPassword = watch('confirmPassword')

  const passwordRequirements = [
    { text: '8 characters minimum', met: password && password.length >= 8 },
    { text: 'a number', met: password && /\d/.test(password) },
    { text: 'a symbol', met: password && /[!@#$%^&*(),.?":{}|<>]/.test(password) }
  ]

  // Calculate progress and color based on met requirements
  const metRequirements = passwordRequirements.filter(req => req.met).length
  const progressPercentage = Math.min((password?.length || 0) * 12.5, 100)
  
  const getProgressBarColor = () => {
    if (metRequirements === 0) return 'bg-red-500'
    if (metRequirements === 1) return 'bg-red-500'
    if (metRequirements === 2) return 'bg-orange-500'
    if (metRequirements === 3) return 'bg-green-500'
    return 'bg-red-500'
  }

  // Password tracking is now working with color-coded progress bar

  // Update hasPasswordValue when password changes
  useEffect(() => {
    setHasPasswordValue(!!password && password.length > 0)
  }, [password])

  // Trigger validation when password fields change
  useEffect(() => {
    if (password && confirmPassword) {
      trigger('confirmPassword')
    }
  }, [password, confirmPassword, trigger])

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true)
    setSubmitError(null)
    setSuccessMessage(null)
    clearErrors()

    try {
      // Step 1: Create user account
      const { data: signUpData, error: signUpError } = await signUp({
        email: data.email,
        password: data.password,
        fullName: data.fullName,
        organizationName: data.organizationName,
        position: data.position
      })

      if (signUpError) {
        if (signUpError.message.includes('User already registered')) {
          setError('email', {
            type: 'manual',
            message: 'Email already in use'
          })
        } else if (signUpError.message.includes('Invalid email')) {
          setError('email', {
            type: 'manual',
            message: 'Invalid email address'
          })
        } else {
          setSubmitError(signUpError.message)
        }
        return
      }

      // Step 2: If plan was selected, create company and redirect to Stripe
      if (planFromUrl && signUpData?.user) {
        const userId = signUpData.user.id

        // Map plan names to subscription tiers
        const planToTierMap: Record<string, 'essentials' | 'enhanced' | 'premium'> = {
          'essentials': 'essentials',
          'proactive': 'enhanced',
          'premium': 'premium'
        }
        const tier = planToTierMap[planFromUrl.toLowerCase()] || 'essentials'

        const companyName = data.organizationName.trim()
        const companySlug = generateCompanySlug(companyName)

        // Create company
        const company = await createCompany.mutateAsync({
          name: companyName,
          slug: companySlug,
          subscription_tier: tier
        })

        // Link user to company
        await updateUser.mutateAsync({
          id: userId,
          company_id: company.id,
          full_name: data.fullName
        })

        // Update user metadata with company info
        await supabase.auth.updateUser({
          data: {
            company_id: company.id,
            company_name: companyName
          }
        })

        // Create Stripe checkout session
        const { data: stripeData, error: stripeError } = await supabase.functions.invoke('stripe-create-checkout-session', {
          body: {
            tier: tier,
            company_id: company.id,
            user_email: data.email,
            success_url: `${window.location.origin}/subscription/success?email=${encodeURIComponent(data.email)}&password=${encodeURIComponent(data.password)}`,
            cancel_url: `${window.location.origin}/login`
          }
        })

        if (stripeError || !stripeData?.url) {
          setSubmitError('Account created but unable to start checkout. Please login to continue.')
          setTimeout(() => navigate('/login'), 3000)
          return
        }

        // Redirect to Stripe checkout
        window.location.href = stripeData.url
      } else {
        // No plan selected - redirect to login
        setSuccessMessage('Registration successful! Please check your email to verify your account.')
        setTimeout(() => navigate('/login'), 3000)
      }
    } catch (error) {
      console.error('Registration error:', error)
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.'
      setSubmitError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="w-full">
      {/* Title */}
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8 text-center">
        Sign Up
      </h1>

      {/* Submit Error Display */}
      {submitError && (
        <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-xs sm:text-sm text-center">{submitError}</p>
        </div>
      )}

      {/* Success Message Display */}
      {successMessage && (
        <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-600 text-xs sm:text-sm text-center">{successMessage}</p>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
        {/* Full Name Field */}
        <div>
          <label htmlFor="fullName" className="block text-xs sm:text-sm font-medium text-gray-600 mb-2">
            Full Name
          </label>
          <input
            {...register('fullName')}
            type="text"
            id="fullName"
            className="w-full px-4 py-2.5 sm:py-3 bg-white border border-gray-300 rounded-full text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm sm:text-base"
            placeholder="Enter your full name"
          />
          {errors.fullName && (
            <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.fullName.message}</p>
          )}
        </div>

        {/* Organization Name Field */}
        <div>
          <label htmlFor="organizationName" className="block text-xs sm:text-sm font-medium text-gray-600 mb-2">
            Organization Name
          </label>
          <input
            {...register('organizationName')}
            type="text"
            id="organizationName"
            className="w-full px-4 py-2.5 sm:py-3 bg-white border border-gray-300 rounded-full text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm sm:text-base"
            placeholder="Enter your organization name"
          />
          {errors.organizationName && (
            <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.organizationName.message}</p>
          )}
        </div>

        {/* Position/Role Field */}
        <div>
          <label htmlFor="position" className="block text-xs sm:text-sm font-medium text-gray-600 mb-2">
            Position / Role
          </label>
          <input
            {...register('position')}
            type="text"
            id="position"
            className="w-full px-4 py-2.5 sm:py-3 bg-white border border-gray-300 rounded-full text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm sm:text-base"
            placeholder="Enter your position or role"
          />
          {errors.position && (
            <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.position.message}</p>
          )}
        </div>

        {/* Email Field */}
        <div>
          <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-gray-600 mb-2">
            Email
          </label>
          <input
            {...register('email')}
            type="email"
            id="email"
            className="w-full px-4 py-2.5 sm:py-3 bg-white border border-gray-300 rounded-full text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm sm:text-base"
            placeholder="Enter your email"
          />
          {errors.email && (
            <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.email.message}</p>
          )}
        </div>

        {/* Password Field */}
        <div>
          <label htmlFor="password" className="block text-xs sm:text-sm font-medium text-gray-600 mb-2">
            Password
          </label>
          <div className="relative">
            <input
              {...register('password')}
              type={showPassword ? 'text' : 'password'}
              id="password"
              className="w-full px-4 py-2.5 sm:py-3 bg-white border border-gray-300 rounded-full text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 pr-10 text-sm sm:text-base"
              placeholder="Enter your password"
              onFocus={() => setIsPasswordFocused(true)}
              onBlur={() => setIsPasswordFocused(false)}
              onChange={(e) => {
                // Update the form value
                register('password').onChange(e)
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showPassword ? <EyeOff size={18} className="sm:w-5 sm:h-5" /> : <Eye size={18} className="sm:w-5 sm:h-5" />}
            </button>
          </div>
          
          {/* Password Progress Bar - Only show when focused or has value */}
          <div className={`mt-2 transition-all duration-300 ease-in-out overflow-hidden ${
            (isPasswordFocused || hasPasswordValue) 
              ? 'max-h-4 opacity-100' 
              : 'max-h-0 opacity-0'
          }`}>
            <div className="w-full bg-gray-200 rounded-full h-1">
              <div 
                className={`h-1 rounded-full transition-all duration-300 ${getProgressBarColor()}`}
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>

          {/* Password Requirements - Only show when focused or has value */}
          <div className={`mt-3 transition-all duration-300 ease-in-out overflow-hidden ${
            (isPasswordFocused || hasPasswordValue) 
              ? 'max-h-32 opacity-100' 
              : 'max-h-0 opacity-0'
          }`}>
            <div className="space-y-1">
              {passwordRequirements.map((req, index) => (
                <div key={index} className="flex items-center text-sm transition-all duration-200">
                  {req.met ? (
                    <Check size={16} className="text-green-600 mr-2 transition-colors duration-200" />
                  ) : (
                    <div className="w-4 h-4 border border-gray-300 rounded-full mr-2 transition-colors duration-200" />
                  )}
                  <span className={`transition-colors duration-200 ${req.met ? 'text-green-600' : 'text-gray-600'}`}>
                    {req.text}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {errors.password && (
            <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
          )}
        </div>

        {/* Confirm Password Field */}
        <div>
          <label htmlFor="confirmPassword" className="block text-xs sm:text-sm font-medium text-gray-600 mb-2">
            Confirm Password
          </label>
          <div className="relative">
            <input
              {...register('confirmPassword')}
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              className="w-full px-4 py-2.5 sm:py-3 bg-white border border-gray-300 rounded-full text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 pr-10 text-sm sm:text-base"
              placeholder="Confirm your password"
              onChange={(e) => {
                register('confirmPassword').onChange(e)
              }}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showConfirmPassword ? <EyeOff size={18} className="sm:w-5 sm:h-5" /> : <Eye size={18} className="sm:w-5 sm:h-5" />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.confirmPassword.message}</p>
          )}
        </div>

        {/* Register Button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-800 disabled:cursor-not-allowed text-white font-semibold py-2.5 sm:py-3 px-4 rounded-full transition-colors duration-200 text-sm sm:text-base"
        >
          {isLoading ? 'Creating Account...' : 'Register'}
        </button>

        {/* Sign In Link */}
        <div className="text-center">
          <p className="text-gray-600 text-xs sm:text-sm">
            Already have an account?{' '}
            <Link to="/login" className="text-red-600 hover:text-red-500 font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </form>
    </div>
  )
}
